import { Directive, Input, OnDestroy, OnInit, TemplateRef, ViewContainerRef } from '@angular/core';
import * as _ from 'lodash-es';
import { Subject, combineLatest } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { CheckVisibilityService } from 'src/app/shared/common/components/check-visibility.service';
import { MapWorkspaceStreamsService } from 'src/app/shared/common/current-map-workspaces/map-workspace-streams.service';
import { CurrentUserStreamService } from 'src/app/shared/common/current-user/current-user-stream.service';
import { GeneralUtils } from 'src/app/shared/common/utility/general-utils';
import { MapWorkspace } from 'src/app/shared/map-data-services/mapWorkspace/map-workspace';
import { MapWorkspacePermissionType } from 'src/app/shared/map-data-services/mapWorkspace/map-workspace-permission';
import { WorkspaceType } from 'src/app/shared/map-data-services/mapWorkspace/map-workspace.types';
import { User, UserRole } from 'src/app/shared/user/user';

export interface VisibilityConfig {
    visibleModes: MapWorkspacePermissionType[];
    visibleRoles: UserRole[];
    toolbarOption: string;
    toolbarOptionValue: boolean;
    visibleOnlyFor: WorkspaceType[];
}

@Directive({ selector: '[checkVisibility]' })
export class CheckVisibilityDirective implements OnInit, OnDestroy {
    @Input('checkVisibility')
    public config: VisibilityConfig;

    private stop$ = new Subject();
    private isVisible = false;

    constructor(
        private viewContainerRef: ViewContainerRef,
        private templateRef: TemplateRef<any>,
        private mapWorkspaceStreams: MapWorkspaceStreamsService,
        private currentUserStream: CurrentUserStreamService,
        private checkVisibilityService: CheckVisibilityService
    ) {}

    ngOnInit(): void {
        combineLatest([
            this.mapWorkspaceStreams.currentMapWorkspaceStream.pipe(distinctUntilChanged(GeneralUtils.isIdEqual)),
            this.currentUserStream.currentUserWithRoleStream
        ])
            .pipe(takeUntil(this.stop$))
            .subscribe(([mapWorkspace, userInfo]) => {
                this.isVisible = this.shouldShow(mapWorkspace, userInfo);

                this.viewContainerRef.clear();
                if (this.isVisible) {
                    this.viewContainerRef.createEmbeddedView(this.templateRef);
                }
            });
    }

    ngOnDestroy(): void {
        this.stop$.next(null);
    }

    public checkToolbarOptionsVisibility(mapWorkspace: MapWorkspace): boolean {
        return (
            mapWorkspace.isFileViewer &&
            mapWorkspace.options &&
            this.config.toolbarOption &&
            _.get(mapWorkspace.options, this.config.toolbarOption) === this.config.toolbarOptionValue
        );
    }

    public checkVisibleModes(visibleModes: MapWorkspacePermissionType[], mapWorkspace: MapWorkspace): boolean {
        return this.checkVisibilityService.restrictAccess(visibleModes, mapWorkspace);
    }

    public checkVisibleRoles(visibleRoles: UserRole[], userInfo: User): boolean {
        return visibleRoles.indexOf(userInfo?.role) === -1;
    }

    private shouldShow(mapWorkspace: MapWorkspace, userInfo?: User): boolean {
        if (!mapWorkspace) return false;

        // Show DOM element only to defined workspace types.
        // USAGE: *checkVisibility={ visibleOnlyFor: [WorkspaceType.GIS, WorkspaceType.FILE_VIEWER] } / [checkVisibility]={ visibleOnlyFor: [WorkspaceType.GIS, WorkspaceType.FILE_VIEWER] }
        const workspaceTypeRestricted =
            this.config.visibleOnlyFor && !this.config.visibleOnlyFor.includes(mapWorkspace.type);

        // Show DOM element only to defined UserRole.
        // USAGE: *checkVisibility={ visibleRoles: [UserRole.ADMIN, UserRole.USER] } / [checkVisibility]={ visibleRoles: [UserRole.ADMIN, UserRole.USER] }
        const userRoleRestricted =
            this.config.visibleRoles && this.checkVisibleRoles(this.config.visibleRoles, userInfo);

        // TODO: needs cleanup in 'VisibilityConfig' interface - out of scope for this task (TCMAPS-4585)
        // - toolbarOption: 'hideZoomButton' || 'hideSearchBox' || 'hideSelectionTool' || 'hideToolbar' are hardcoded strings that are present in the codebase
        // - toolbarOptionValue: true || false are used to determine visibility

        // Show DOM element only to defined WorkspacePermissionType
        // USAGE: *checkVisibility={ visibleModes: [MapWorkspacePermissionType.READ_ONLY, MapWorkspacePermissionType.FULL_ACCESS] } / [checkVisibility]={ visibleModes: [MapWorkspacePermissionType.READ_ONLY, MapWorkspacePermissionType.FULL_ACCESS] }
        const modeOrToolbarRestricted =
            (this.config.visibleModes || this.config.toolbarOption) &&
            (this.checkVisibleModes(this.config.visibleModes, mapWorkspace) ||
                this.checkToolbarOptionsVisibility(mapWorkspace));

        if (modeOrToolbarRestricted || workspaceTypeRestricted || userRoleRestricted) {
            return false;
        }

        return true;
    }
}
